/**
 * The backend adapter — one interface, two implementations.
 *
 * A "backend" is the thing that turns (system prompt + dimension instruction + diff + output schema)
 * into structured findings. `claude-cli.ts` spends the subscription; `anthropic-api.ts` spends
 * tokens. `probe.ts` decides which, and always says why.
 *
 * ⛔ THE RESULT STAMPS WHICH BACKEND ACTUALLY RAN, not which one was asked for. `on_unavailable =
 * "fallback"` is allowed to degrade a run to a weaker brain; it is never allowed to present the
 * output as though the requested one produced it. Every finding carries this through to triage
 * (docs/spec/config.md §1 `[backend].on_unavailable`).
 *
 * ⛔ CACHE ACCOUNTING IS PART OF THE RESULT TYPE, NOT AN OPTIONAL EXTRA. The AI call log this
 * project is reacting to has "17 columns and not one cache field" (spec.md §7.4), which makes the
 * hit rate unmeasurable rather than merely bad. There is deliberately no way to construct a
 * BackendResult without saying what the cache did.
 */

import { EXIT } from "../config/validate.js"
import type { Backend as BackendKind, EffortLevel } from "../config/schema.js"
import type { CacheStats } from "./cache.js"

export type { BackendKind }

/**
 * The cacheable prefix, split at the points the cache is keyed on.
 *
 * ⭐ Two fields rather than one string, because the layout IS the implementation (design research
 * §7.3). Corpus and rubric are identical for every dimension of every pull request and each gets its
 * own 1-hour breakpoint; concatenating them here would throw away a breakpoint for nothing.
 */
export interface SystemPrompt {
  /** [A] Repo conventions and spec excerpts. Changes when the reviewed repo's rules change. */
  readonly corpus: string
  /** [B] The shared review rubric — what a finding is, and the nine fields it must carry. */
  readonly rubric: string
}

/** A JSON Schema document. Kept opaque: the shape is the caller's contract with the model. */
export type JsonSchema = Readonly<Record<string, unknown>>

/**
 * One dimension's worth of work.
 *
 * ⛔ FIELD ORDER HERE IS CACHE ORDER: corpus → rubric → diff → dimensionInstruction. The instruction
 * is last and uncached because it is the only part that varies per call. Put it before the diff and
 * every request pays a fresh cache write and never gets a read (design research §7.3, rule 3).
 */
export interface BackendRequest {
  /** Stable key used in the cache ledger and in every finding's provenance. */
  readonly dimensionId: string
  readonly systemPrompt: SystemPrompt
  /** [C] The diff chunk under review. Shared by all dimensions of one pull request → 5m TTL. */
  readonly diff: string
  /** [D] ⛔ Last, and never cached. */
  readonly dimensionInstruction: string
  /** Grammar-constrained output. Not a request in prose for JSON. */
  readonly outputSchema: JsonSchema
  /**
   * Where the CLI backend may read code from — the sibyl clone, checked out at the reviewed sha.
   * The API backend ignores it; it has no tools.
   */
  readonly workdir?: string
  /** Overrides the backend's configured model for this call. Held CONSTANT across a fan-out. */
  readonly model?: string
  readonly effort?: EffortLevel
}

export interface TokenUsage {
  readonly inputTokens: number
  readonly outputTokens: number
}

/**
 * ⚠️ `stop` is not always success. A refusal returns HTTP 200 with `stop_reason: "refusal"` and
 * output that may not match the schema; a truncation returns `max_tokens` with a partial object.
 * Both look like a successful call to anything that only checks the status code.
 */
export type StopReason = "end_turn" | "max_tokens" | "refusal" | "stop_sequence" | "tool_use" | "other"

export interface BackendResult {
  /** ⛔ Which backend ACTUALLY ran. Never the one that was requested. */
  readonly backend: BackendKind
  /**
   * Set only when a fallback happened. ⛔ Present means the run was degraded: the caller must stamp
   * it onto every finding rather than reporting a full review.
   */
  readonly degradedFrom?: BackendKind
  readonly dimensionId: string
  /** ⛔ The model that ACTUALLY answered, never the one that was asked for. */
  readonly model: string
  /**
   * Set when the configured model refused or errored and a fallback answered instead.
   *
   * ⛔ Present means the review was produced by a different model than the configuration names. It
   * is reported rather than absorbed: a finding's provenance has to be true for the finding to be
   * checkable a week later.
   */
  readonly modelFallbackFrom?: string
  /** Why the first model did not answer, in its own words. */
  readonly modelFallbackReason?: string
  readonly effort: string
  /** Parsed structured output. Validated against `outputSchema` by the model, not by us. */
  readonly output: unknown
  /** The raw text the backend produced, kept for the audit trail. */
  readonly raw: string
  readonly stopReason: StopReason
  readonly usage: TokenUsage
  /** ⛔ Never optional. See the file header. */
  readonly cache: CacheStats
  readonly durationMs: number
  /** Present when the backend reports it. The CLI does; the API leaves it to our own arithmetic. */
  readonly costUsd?: number
  /**
   * ⚠️ Tools the backend wanted and was refused. A denied `Read` means the review saw less code than
   * it claims to have seen, so this is surfaced rather than counted as a clean run.
   */
  readonly permissionDenials: readonly string[]
}

/** One interface. Both implementations satisfy exactly this. */
export interface Backend {
  readonly kind: BackendKind
  run(request: BackendRequest): Promise<BackendResult>
}

/** Exit 69 per docs/spec/config.md §6: a backend or credential problem, not a configuration one. */
export class BackendError extends Error {
  readonly exitCode = EXIT.UNAVAILABLE
  constructor(
    message: string,
    readonly backend: BackendKind,
    /** The literal output that produced this verdict, so the error is checkable rather than asserted. */
    readonly evidence: string = "",
  ) {
    super(message)
    this.name = "BackendError"
  }
}

/**
 * The credential is present and was rejected.
 *
 * ⛔ Distinct from {@link ProbeFailedError} on purpose. Telling a user their credential is bad when
 * the probe merely failed to execute sends them to rotate a working key.
 */
export class BackendAuthError extends BackendError {
  constructor(message: string, backend: BackendKind, evidence = "") {
    super(message, backend, evidence)
    this.name = "BackendAuthError"
  }
}

/**
 * The probe did not RUN — the binary is missing, the spawn failed, it timed out, or it printed
 * something we cannot interpret.
 *
 * ⛔ This is reported as "probe failed", never as "bad credential". Measured in verdict and
 * recorded in docs/spec/config.md §3: the CLI prints "Not logged in" on stdout and exits 0, so exit
 * status carries no auth information in either direction. A probe that cannot run has learned
 * nothing about the credential.
 */
export class ProbeFailedError extends BackendError {
  constructor(message: string, backend: BackendKind, evidence = "") {
    super(message, backend, evidence)
    this.name = "ProbeFailedError"
  }
}
